<script>
  import axios from "axios"
  import { push, querystring } from "svelte-spa-router"
  import { onMount, onDestroy } from "svelte"
  import bulkJson from "../../Stores/jsonBulkData"
  import { tweened } from "svelte/motion"
  import { queryStringToJSON } from "../../../../../Script/queryString"
  import { cubicOut } from "svelte/easing"
  import { isStringifiedObject, validateFile } from "../../Script/bulkUpload"
  import Footer from "../../../../../Shared/footer.svelte"
  import Navbar from "../../../../../Shared/Navbar/index.svelte"
  import ApplicationState from "../../../../../Stores/applicationStates"
  import FilePage from "./Components/filePage.svelte"
  import notify from "../../../../../Script/notify"
  import Counter from "../../Shared/counter2.svelte"
  import Loader from "../../../../../Shared/Slots/loader.svelte"
  import ConfirmationModal from "../../Shared/confirmationModal.svelte"
  import Tab from "../../Shared/tab.svelte"
  import { loadQR } from "../../Script/bulkUpload"
  import { loadJs } from "../../../../../Script/loadLibrary"
  import Progress from "../../Shared/progress.svelte"
  import { fetchBulkStatus, fetchBulkTemplate } from "../../Script/bulkUpload"
  import RetryPage from "../../Shared/retryPage.svelte"

  /**
   * @type {Array<import("../../Script/document").bulkDocument>}
   */
  let jsonForFile = []
  /**
   * @type {Array<import("../../Script/document").bulkDocument>}
   */
  let fileInProcess = []
  /**
   * @type {String} active tab while generating files
   */
  let activeItem = "Publishing Status"
  /**
   * @type {"start"|"upload"|"processing"|"completed"|"retry"} active item in document status tab
   */
  let activeDoc = "start"
  /**
   * @type {Number} number of files to be uploaded
   */
  let uploadedFiles = 0
  /**
   * @type {Number} number of files failed to upload
   */
  let failedDocuments = 0
  /**
   * @type {Number} the total number of documents to be uploaded
   */
  let totalDoc = 0
  /**
   * @type {Number} the count which reflects the increment limit of the progress bar
   */
  let incrementLimit
  /**
   * @type {String} activeColor of the tabs
   */
  let activeColor
  /**
   * @type {Number} initial width of the progress bar
   */
  let width = 0
  /**
   * @type {Boolean} whether to show toggle or not
   */
  let showLoading = false
  /**
   * @type {String} the text to be shown while loading
   */
  let loadingText
  /**
   * @type {Boolean} whether to show confirmation modal for proceeding sign and publish
   */
  let showConfirmationModal = false
  /**
   * @type {File} file to be uploaded by the user
   */
  let files
  /**
   * @type {Array<File>} list of arrays consists of files uploaded by the user
   */
  let fileArray = []
  /**
   * @type {Array<String>} array of filenames not matched with the spreadsheet entries
   */
  let errorFiles = []
  /**
   * @type {Boolean} whether to display the error files or not
   */
  let displayErrorFiles = false
  /**
   * @type {Object} stores the query value in the current url location
   */
  let query
  /**
   * @type {string} name of the start button based on the condition
   */
  let startBtnName = "Start"
  /**
   * Creates a tweened value representing the width of a progress indicator.
   *
   * @param {number} initialValue - The initial value of the tweened width.
   * @param {Object} options - Options for configuring the tween animation.
   * @param {number} options.duration - The duration of the tween animation in milliseconds.
   * @param {function} options.easing - The easing function used for the animation.
   * @returns {Object} - A tweened value object that represents the width of the progress indicator.
   */
  const widthofProgress = tweened(0, {
    duration: 400,
    easing: cubicOut,
  })

  onMount(async () => {
    try {
      // Set showLoading to true to indicate loading state
      showLoading = true

      // Set loadingText to indicate the current loading step
      loadingText = `Fetching Bulk Upload data&nbsp;`

      // Parse the query parameters from the URL using the `queryStringToJSON` function.
      query = queryStringToJSON($querystring)

      // If the 'id' parameter is missing in the query, redirect the user to "/publish/bulk".
      if (!query.id) throw "Invalid URL"

      // Fetch data about the bulk publishing status using the "id" from the query
      let apiData = await fetchBulkStatus(query.id)

      // Redirect based on the status of the bulk publishing
      if (apiData.status == "active" || apiData.status == "published" || apiData.status == "revoked") {
        push(`/documents/bulk/view-report?id=${query.id}`)
        return
      }

      if (apiData.status == "uploaded") {
        push(`/publish/bulk/sign-publish?id=${query.id}`)
        return
      }

      // If bulkData indicates that a file should be generated, redirect to the appropriate route.
      if (apiData.generateFile) {
        push(`/publish/bulk/generate-file?id=${query.id}`)
        return
      }

      // Fetch bulk template data based on the 'id' parameter from the query.
      let bulkData = await fetchBulkTemplate(query.id)

      // Store the array of documents from the fetched bulkData.
      $bulkJson = [...bulkData.documents]

      // Assign the total number of documents in $bulkJson to the 'totalDoc' variable.
      totalDoc = $bulkJson.length

      // Filter out documents that have already been assigned a file hash.
      let tobeProcessed = $bulkJson.filter((a) => !a.fileHash)

      if (tobeProcessed.length != $bulkJson.length) startBtnName = "Resume"

      // Calculate the number of uploaded files based on the remaining documents to be processed.
      uploadedFiles = $bulkJson.length - tobeProcessed.length

      // Iterate through each document in $bulkJson.
      for (let i = 0; i < $bulkJson.length; i++) {
        $bulkJson[i].isDocumentFailed = false
        // Parse the 'filename' property from JSON format to a JavaScript object.
        if (isStringifiedObject($bulkJson[i].filename)) {
          $bulkJson[i].filename = JSON.parse($bulkJson[i].filename)
        }
      }
    } catch (error) {
      const info = "PAGE: Upload Flow (Bulk Upload)\nEVENT: onMount"
      notify.danger(error, info)

      // Redirect to the bulk publishing page in case of an error
      push("/publish/bulk")
    } finally {
      // Set showLoading back to false to end the loading state
      showLoading = false
    }
  })

  /**
   * Function for initiating the whole process
   */
  const initiateProcess = () => {
    // let tobeProcessed = $bulkJson.filter((a) => !a.fileHash)
    // Check if there is data in fileInProcess, jsonForFile
    if (fileInProcess.length || jsonForFile.length) assignData2Array()
    else {
      clearInterval(timer)
      console.timeEnd("initiate")
    }
  }

  onDestroy(() => {
    clearInterval(timer)
  })

  /**
   * Function for assigning data to respective array
   */
  const assignData2Array = () => {
    // Get the number of available hardware threads or cores
    let hwc = window.navigator.hardwareConcurrency

    // if (fileInProcess.length || jsonForFile.length) {
    // Assign files to the fileInProcess array if it has fewer elements than the available hardware threads
    if (fileInProcess.length < hwc) {
      let fArray = jsonForFile.splice(0, hwc - fileInProcess.length)

      // Add the extracted elements to the fileInProcess array
      fileInProcess = [...fileInProcess, ...fArray]

      // Process each element of fArray by calling the getFileHash function
      for (let i = 0; i < fArray.length; i++) {
        getFileHash(fArray[i])
      }
    }

    // Initiate the process by requesting the next animation frame
    // window.requestAnimationFrame(initiateProcess)
  }

  /**
   * Function for initiate the process for getting fileHash
   * @param {import("../../Script/document").bulkDocument} data
   */
  const getFileHash = async (data) => {
    try {
      // Call the 'generateFileHash' function to get the file hash from an API
      let apiData = await generateFileHash(data.id, data.filename.filename)

      // Find the corresponding entry in '$bulkJson' based on its ID
      let tobeUpdated = $bulkJson.filter((a) => a.id == data.id)[0]

      // Update the 'fileHash', 'filename', 'file', and 'state' properties of the corresponding entry
      tobeUpdated.fileHash = apiData.fileHash
      tobeUpdated.filename = apiData.filename

      // increment the 'uploadedFiles' counter to keep track of the number of files being uploaded
      uploadedFiles++
      changeWidth()

      // Replace the old entry with the updated one in the '$bulkJson' array
      $bulkJson = [...$bulkJson.filter((a) => a.id != data.id), tobeUpdated]
    } catch (error) {
      // Find the corresponding entry in '$bulkJson' based on its ID
      // failedDocuments++
      // Find the corresponding entry in '$bulkJson' based on its ID
      let tobeUpdated = $bulkJson.filter((a) => a.id == data.id)[0]

      tobeUpdated.errorMsg = error
      tobeUpdated.isDocumentFailed = true

      // Replace the old entry with the updated one in the '$bulkJson' array
      $bulkJson = [tobeUpdated, ...$bulkJson.filter((a) => a.id != data.id)]
      failedDocuments = $bulkJson.filter((a) => a.isDocumentFailed).length
      changeWidth()
    } finally {
      // Remove the current file entry from the 'fileInProcess' array based on its ID
      fileInProcess = fileInProcess.filter((a) => a.id != data.id)
    }
  }

  /**
   * Change the width of a progress bar based on the increment limit and handle completion states.
   * @param {number} times - The number of times to increment the width (default is 1).
   */
  const changeWidth = (times = 1) => {
    // Check if the increment limit exists
    if (incrementLimit) {
      // Increase the width by the increment limit
      width = width + incrementLimit * times
      // Set the updated width to a variable called "widthofProgress"
      widthofProgress.set(width)

      if (Math.round(width) == 100) checkStatus()
    }
  }

  const checkStatus = () => {
    // Check if the width is rounded to 100%
    // Check if there are failed documents
    if (failedDocuments) {
      if (failedDocuments == totalDoc) {
        // Reset error files and set the activeDoc to "retry"
        errorFiles = []
        activeDoc = "retry"
        return
      } else {
        // Reset the progress and set the activeDoc to "upload"
        widthofProgress.set(0)
        activeDoc = "upload"
        files = undefined
        // Display error files if there are any, otherwise hide them
        displayErrorFiles = errorFiles.length ? true : false
        return
      }
    } else {
      // Check if all files have been successfully uploaded
      if (uploadedFiles == totalDoc) {
        // Reset error files and set the activeDoc to "completed"
        errorFiles = []
        activeDoc = "completed"
        return
      } else {
        // Reset the progress and set the activeDoc to "upload"
        widthofProgress.set(0)
        activeDoc = "upload"
        files = undefined
        // Display error files if there are any, otherwise hide them
        displayErrorFiles = errorFiles.length ? true : false
        return
      }
    }
  }

  /**
   * Function for generating fileHash
   * @param {String} id - its the generated document id
   * @param {File} file - generated file for uploading process
   */
  const generateFileHash = async (id, file) => {
    return new Promise(async (resolve, reject) => {
      try {
        if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
        const { data } = await axios.patch(
          `/api/swagger/v2/documents/${id}/add-file`,
          {
            file: file,
          },

          {
            progress: function (progressEvent) {
              let percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            },
            onUploadProgress: function (progressEvent) {
              let percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            },
            headers: {
              "Content-Type": "multipart/form-data",
              "x-access-token": $ApplicationState.token,
            },
          }
        )

        // Check if the response has an error and is not successful
        if (data.error && !data.success) {
          throw data.errorCode // Throw the error code
        }
        resolve(data)
      } catch (error) {
        reject(error)
      }
    })
  }

  let timer

  const whileStart = () => {
    // setting the increment limit based on the jsonData length
    incrementLimit = 100 / jsonForFile.length

    console.time("initiate")

    // Set the activeDoc status to "processing"
    activeDoc = "processing"

    // Call the initiateProcess function
    // initiateProcess()

    timer = setInterval(initiateProcess, 30)
  }

  $: changeActiveColor(activeDoc)

  /**
   * Function for changing the active color of the tabs
   * @param {String} activeDoc the current state of the document status
   */
  const changeActiveColor = (activeDoc) => (activeColor = activeDoc == "completed" ? "#22C55E" : "#FF6A00")

  /**
   * Function for initiate the process of uploading files
   */
  const uploadFiles = async () => {
    // Initialize variables for storing data and counters.
    width = 0
    fileArray = []

    // Copy the array of files to the 'fileArray'.
    fileArray = [...files]

    // Iterate through each document in $bulkJson.
    for (let i = 0; i < $bulkJson.length; i++) {
      if (!$bulkJson[i].isDocumentFailed) {
        // Iterate through each key-value pair in the 'filename' object of the document.
        for (const [key, value] of Object.entries($bulkJson[i].filename)) {
          // Check if the value is a string (indicating a missing file).
          if (typeof value == "string") {
            // Find the file in the 'fileArray' that matches the filename value.
            let isFileFound = fileArray.filter((file) => file.name === value)
            if (isFileFound.length) {
              try {
                let file = await validateFile(isFileFound[0])
                // Update the 'filename' property in the document with the found file.
                $bulkJson[i].filename.filename = file

                // Add the document to the 'jsonForFile' array for uploading.
                jsonForFile.push($bulkJson[i])

                // Remove the found file from the 'fileArray'.
                fileArray = fileArray.filter((file) => file.name !== value)
              } catch (error) {
                const info = "PAGE: Upload Flow (Bulk Upload)\nEVENT: While matching file details with the uploaded file"
                notify.danger(error, info)
              }
            }
          }
        }
      }
    }

    // Create an array of error file names from the remaining files in 'fileArray'.
    errorFiles = Array.from(fileArray).map((file) => file.name)

    if (jsonForFile.length) {
      whileStart()
      // Set 'displayErrorFiles' to false to indicate no display of error files.
      displayErrorFiles = false
    } else {
      // If no documents are ready for upload, set 'displayErrorFiles' to true.
      displayErrorFiles = true
      // Reset the 'files' array to undefined.
      files = undefined
    }

    failedDocuments = $bulkJson.filter((a) => a.isDocumentFailed).length
  }

  /**
   * Function for downloading the Document or qr of the documents based on type
   */
  const downloadQr = async () => {
    try {
      // Set a flag to show loading while the download is in progress
      showLoading = true

      // Define loading text based on the download type (qr or documents)
      loadingText = "Downloading Qr&nbsp;"

      // Load JSZip library if it's not already loaded
      await loadJs("JSZip", "assets/lib/jszip.min.js")

      // Load FileSaver library if it's not already loaded
      await loadJs("FileSaver", "assets/lib/FileSaver.min.js")

      // Create a new JSZip instance
      const zip = new JSZip()

      // Add a 'Read Me.txt' file to the zip with some initial content
      zip.file("Read Me.txt", "Documents Generated by Docchain.io\n")

      // Create a folder named 'images' inside the zip
      let img = zip.folder("images")

      // Loop through an array ($bulkJson) and add files to the zip based on the download type

      for (let i = 0; i < $bulkJson.length; i++) {
        if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
        let qr = await loadQR($bulkJson[i].url)
        // For QR download, add an image file from the base64 data in $bulkJson array
        img.file($bulkJson[i].recName + "-" + Math.floor(Math.random() * 10000) + ".jpg", qr.slice(22), { base64: true })
      }
      zip.generateAsync({ type: "blob" }).then(function (content) {
        saveAs(content, "Documents.zip") // Save the zip as 'Documents.zip'
      })
    } catch (error) {
      const info = "PAGE: Upload Flow (Bulk Upload)\nEVENT: While downloading QR zip"
      // If there's an error, log the error and display an error notification
      notify.danger(error, info)
    } finally {
      // Reset the loading flag to false when download is complete or if there's an error
      showLoading = false
    }
  }

  /**
   * Navigate to the "Sign & Publish" page based on the bulk publishing status.
   */
  const navigateSignPublish = async () => {
    try {
      showLoading = true
      // Fetch bulk publishing status using the "id" from the query
      let apiData = await fetchBulkStatus(query.id)

      // Check if the bulk publishing status is "uploaded"
      if (apiData.status == "uploaded") {
        // Show a confirmation modal to proceed with signing and publishing
        showConfirmationModal = true
      } else {
        // Reload the page if the status is not "uploaded"
        window.location.reload()
      }
    } catch (error) {
      const info = "PAGE: Upload Flow (Bulk Upload)\nEVENT: While updating bulk status before navigating to Sign and Publish"
      notify.danger(error, info)
      push("/publish/bulk")
    } finally {
      showLoading = false
    }
  }
</script>

<svelte:head>
  <title>Bulk Upload</title>
</svelte:head>

{#if showConfirmationModal}
  <ConfirmationModal
    title="Confirmation for Sign and Publish"
    content="I have thoroughly reviewed all documents and am prepared to proceed with Signing and Publishing."
    {query}
    on:click={() => {
      showConfirmationModal = false
    }}
    on:confirm={() => {
      showConfirmationModal = false
      push(`/publish/bulk/sign-publish?id=${query.id}`)
    }}
  />
{/if}

<div class="relative min-h-screen w-full overflow-hidden bg-[#f7f7f7] pb-24 dark:bg-bodyblack">
  <Navbar />

  <div class="flex px-6 pb-12 pt-9">
    <div class="relative mx-auto flex w-full max-w-[920px] flex-col justify-between rounded-md bg-white dark:bg-mdGray">
      <Tab {activeColor} bind:activeItem {activeDoc} />

      {#if showLoading}
        <div class="flex h-full items-center justify-center text-base text-gray-400">
          <Loader>
            <span class="flex">
              {@html loadingText}
            </span>
          </Loader>
        </div>
      {/if}

      {#if !showLoading}
        <div class="flex h-full w-full select-none flex-col items-center justify-center p-6 mobile:p-12">
          <!-- <div class=" hidden w-full justify-center gap-6 md:flex">
            <Counter titleText="TOTAL<br>DOCUMENTS" count={totalDoc} innerColor="inset 0px 20px 40px 0px rgba(255, 118, 142, 0.3)" gradient="#ff43c0 0%, #ffa95c 100%" />
            <Counter titleText="UPLOADED<br>FILES" count={uploadedFiles} innerColor="inset 0px 20px 40px 0px rgba(58,212,173,0.3)" gradient="#39a3ff 0%, #3cff53 100%" />
            <Counter titleText="DOCUMENTS<br>FAILED" count={failedDocuments} innerColor="inset 0px 20px 40px 0px rgba(255,81,47,0.3)" gradient="#FF512F 0%, #DD2476 100%" />
          </div>
          <div class=" flex w-full flex-col justify-center gap-6 md:hidden">
            <Counter titleText="TOTAL<br>DOCUMENTS" count={totalDoc} color={"#ff6a00"} gradient={"#f8a500, #ff6a00"} icon={"fa-solid fa-file-lines"} />
            <Counter titleText="UPLOADED<br>FILES" count={uploadedFiles} color={"#00D515"} gradient={"#58FE69, #00D515"} icon={"fa-solid fa-upload"} />
            <Counter titleText="DOCUMENTS<br>FAILED" count={failedDocuments} color={"#fe036a"} gradient={"#f99dbc, #f5347f"} icon={"fa-solid fa-file-exclamation"} />
          </div> -->

          <div class="grid w-full grid-cols-3 justify-center gap-3 xl:gap-6">
            <Counter titleText="TOTAL<br>DOCUMENTS" count={totalDoc} span="col-span-3" color="#ff6a00" gradient="#f8a500, #ff6a00" icon="fa-file-lines" />
            <Counter titleText="UPLOADED<br>FILES" count={uploadedFiles} span="col-span-3" color="#00D515" gradient="#58FE69, #00D515" icon="fa-upload" />
            <Counter titleText="DOCUMENTS<br>FAILED" count={failedDocuments} span="col-span-3" color="#F03232" gradient="#F87A7A, #F03232" icon="fa-file-exclamation" />
          </div>

          {#if activeItem == "Document Status"}
            <FilePage
              on:release={() => {
                totalDoc--
                if (totalDoc == 0) {
                  notify.info("No Documents Left in the bulk Template as you released all document")
                  push("/publish/bulk")
                  return
                }
                // if (uploadedFiles != 0) uploadedFiles--
                failedDocuments = $bulkJson.filter((a) => a.isDocumentFailed).length
                checkStatus()
              }}
            />
          {:else}
            <!-- <h1>dfhgd</h1> -->

            {#if activeDoc == "start"}
              <div class="mt-6 flex w-full justify-center text-center text-base text-gray-400 mobile:w-3/4">
                {#if startBtnName == "Start"}
                  <h1>To begin the process, click the start button for uploading documents.If you intend to acquire a QR code for your documents, simply select the download button provided at this location.</h1>
                {:else}
                  <h1>In order to proceed with the procedure, click on the "resume" button to upload the remaining documents.</h1>
                {/if}
              </div>
              <div class="mt-6 flex gap-3">
                {#if startBtnName == "Start"}
                  <button on:click={downloadQr} type="button" class="normal-blue-btn disabled:text-white-200/50 disabled:cursor-not-allowed disabled:bg-blue-200/50">
                    <span><i class="fa-solid fa-qrcode" /></span>
                    <span class="hidden select-none md:block">&nbsp;&nbsp;Download&nbsp;QR&nbsp;</span>
                  </button>
                {/if}
                <button
                  on:click={() => {
                    activeDoc = "upload"
                  }}
                  type="button"
                  class="flex h-9 w-12 min-w-[36px] items-center justify-center rounded-md border border-[#a0a1a580] md:w-32 {startBtnName == 'Start' ? 'bg-green-500 hover:bg-green-600 active:bg-green-500 disabled:bg-green-500/50' : 'bg-blue-500 hover:bg-blue-600 active:bg-blue-500 disabled:bg-blue-500/50'} px-2 text-sm font-medium text-white hover:text-white focus:outline-none disabled:cursor-not-allowed lg:px-4"
                >
                  <span>
                    <i class="fa-solid fa-circle-play" />
                  </span>
                  <span class="hidden select-none md:block">&nbsp;&nbsp;{startBtnName}&nbsp;</span>
                </button>
              </div>
            {:else if activeDoc == "upload"}
              <div class="mt-6 flex flex-col items-center gap-3">
                <h1 class="text-xl font-bold text-gray-800 dark:text-white">Upload Files</h1>
                <h1 class="text-sm font-medium text-gray-200">Make sure that the Files have the same filename as mentioned in the submitted spreadsheet.</h1>
              </div>

              {#if files}
                <div class="relative mx-auto mt-6 flex w-full flex-col items-center justify-center gap-3 rounded-md border border-dashed border-gray-200 px-12 py-12 hover:border-gray-200/50 hover:bg-[#f7f7f7] dark:bg-darkgray sm:w-1/2">
                  <div class="text-3xl text-gray-200">
                    <i class="fa-solid fa-file fa-xl lg:fa-2xl" />
                  </div>
                  <!-- svelte-ignore a11y-click-events-have-key-events -->
                  <div
                    on:click={() => {
                      files = undefined
                    }}
                    class="absolute right-1 top-1 cursor-pointer text-gray-200 hover:text-red-500 active:text-red-500"
                  >
                    <i class="fa-solid fa-circle-xmark fa-lg" />
                  </div>
                  <span class="md:txet-base text-center text-sm font-medium text-gray-200">{files.length} files Uploaded</span>
                </div>
              {:else}
                <div class="relative mx-auto mt-6 flex w-full flex-col items-center justify-center gap-3 rounded-md border border-dashed border-gray-200 px-12 py-12 hover:border-gray-200/50 sm:w-1/2">
                  <input bind:files multiple type="file" accept="image/*" class="absolute inset-0 cursor-pointer opacity-0" />
                  <span class="md:txet-base text-center text-sm font-medium text-gray-200">{$bulkJson.filter((a) => typeof a.filename == "string").length} / {$bulkJson.length}</span>
                  <div class="text-3xl text-gray-200">
                    <i class="fa-solid fa-cloud-arrow-up fa-xl lg:fa-2xl" />
                  </div>
                  <span class="md:txet-base text-center text-sm font-medium text-gray-200">Select a file by dragging and dropping it, or Click here to upload</span>
                  <span class="text-center text-xs font-normal text-gray-100 md:text-sm">Supported formats: jpg, jpeg, png.</span>
                </div>
              {/if}

              {#if displayErrorFiles && !files}
                <div class="mt-6 w-full">
                  {#each errorFiles as file}
                    <div class="mb-3 flex w-full items-center gap-3 rounded-md bg-[#fff3cd] px-2 py-3 text-base font-medium text-[#856404]">
                      <div class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[#856404] text-white">
                        <i class="fa-solid fa-triangle-exclamation text-xl" />
                      </div>
                      <h1 class="wordBreak text-base font-medium">
                        <strong>{file}</strong>
                        not matched with the entry in the spreadsheet
                      </h1>
                    </div>
                  {/each}
                </div>
              {/if}

              <div class="mt-3">
                <button on:click={uploadFiles} disabled={!files} type="button" class="disabled:text-white-200/50 flex h-9 min-w-[36px] items-center justify-center rounded-md border border-[#a0a1a580] bg-blue-500 px-4 text-sm font-medium text-white hover:bg-blue-600 hover:text-white focus:outline-none active:bg-blue-500 disabled:cursor-not-allowed disabled:bg-gray-200/50">
                  <span><i class="fa-solid fa-upload" /></span>
                  <span class="hidden select-none md:block">&nbsp;&nbsp;Upload&nbsp;File&nbsp;</span>
                </button>
              </div>
            {:else if activeDoc == "processing"}
              <Progress width={$widthofProgress} content="File Uploading in Progress" />
            {:else if activeDoc == "retry"}
              <RetryPage content="A few documents encountered failures during the uploading process. Please consider retrying to proceed." />
            {:else}
              <div class="mt-12 flex w-1/2 items-center justify-center gap-3 text-center text-3xl font-bold text-green-500">
                <i class="fa-solid fa-circle-check" />
                <h1 class="text-2xl">Process Completed</h1>
              </div>
              <div class="mt-6 flex w-1/2 justify-center text-center text-base text-gray-400">
                <h1>The files have been successfully uploaded. If everything looks good, you can proceed to sign and publish.</h1>
              </div>
              <div class="mt-12 flex w-full justify-center gap-3">
                <button on:click={navigateSignPublish} type="button" class="disabled:text-white-200/50 flex h-9 items-center justify-center rounded-md border border-[#a0a1a580] bg-green-500 px-4 text-sm font-medium text-white hover:bg-green-600 hover:text-white focus:outline-none active:bg-green-500 disabled:cursor-not-allowed disabled:bg-green-200/50">
                  <span><i class="fa-solid fa-upload" /></span>
                  <span class="select-none">&nbsp;&nbsp;Sign&nbsp;and&nbsp;Publish&nbsp;</span>
                </button>
              </div>
            {/if}
          {/if}
        </div>
      {/if}

      {#if activeItem == "Publishing Status"}
        <h1 class="select-none pb-6 text-center text-base font-medium text-red-500 mobile:pb-12">Please don't refresh the page while in progress.</h1>
      {/if}
    </div>
  </div>

  <Footer />
</div>

<style lang="postcss">
  .normal-blue-btn {
    @apply flex h-9 items-center justify-center rounded-md border border-[#a0a1a580] bg-blue-500 px-4 text-sm font-medium text-white hover:bg-blue-600 hover:text-white focus:outline-none active:bg-blue-500;
  }
</style>
